import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { switchMap } from 'rxjs/operators';
import { tap } from 'rxjs/operators';

export interface User {
  username: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  getUser(): Observable<User> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<User>(`${environment.apiUrl}/users`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }),
      tap(user => {
        this.userSubject.next(user);
      })
    );
  }

  async getCurrentUser(): Promise<User | null> {
    let isAuthenticated = await this.authService.isAuthenticated().toPromise();
    if (!isAuthenticated) {
      return Promise.resolve(null);
    }
    if(this.userSubject.value != null) {
      return this.userSubject.value;
    }
    await this.getUser().toPromise();
    return this.userSubject.value;
  }

  updateUser(patch: { name?: string; password?: string }): Observable<User> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token =>
        this.http.patch<User>(`${environment.apiUrl}/users`, patch, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      ),
      tap(user => this.userSubject.next(user))
    );
  }

  activateEmail(activationKey: string): Observable<void> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token =>
        this.http.post<void>(`${environment.apiUrl}/users/activate/${activationKey}`, null, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      )
    );
  }
} 